import Button from "components/Button/Button";
import Card from "components/Card/Card";
import type { OptionType } from "components/inputs";
import { Checkbox, Select, TextInput } from "components/inputs";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetClientsQuery } from "services/client-api";
import { useCreateRreMutation } from "services/rre-api";
import type { ClientType } from "types/DataTypes";
import { useToken } from "utils/context";
import { hasApiError } from "utils/error-utils";

import KaboodleModal from "./KaboodleModal";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  rre_id: string;
  name: string;
  client_id: OptionType;
  reporting_existed_prior_to_onboarding: boolean;
};

const CreateRREModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { accessToken } = useToken();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      rre_id: "",
      name: "",
      client_id: null,
      reporting_existed_prior_to_onboarding: false,
    },
  });

  const [createRre, { error, isSuccess }] = useCreateRreMutation();

  const [clientOptions, setClientOptions] = useState<OptionType[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState<string | null>(null);

  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setClientSearchTerm(value);
      }, 500),
    [],
  );

  const { data: clients, isFetching: isClientsFetching } = useGetClientsQuery(
    { searchTerm: clientSearchTerm },
    { skip: !clientSearchTerm },
  );

  useEffect(() => {
    if (clients) {
      setClientOptions(
        clients.results?.map((client: ClientType) => ({
          label: client.name,
          value: client.id,
        })) ?? [],
      );
    }
  }, [clients]);

  useEffect(() => {
    if (hasApiError(error)) {
      Object.entries(error.data).forEach(([field, messages]) => {
        setError(field as keyof FormData, {
          type: "manual",
          message: Array.isArray(messages) ? messages[0] : messages,
        });
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      const passedData = {
        rre_id: Number(data.rre_id),
        name: data.name,
        client_id: data.client_id.value.toString(),
        reporting_existed_prior_to_onboarding:
          data.reporting_existed_prior_to_onboarding,
      };
      try {
        const result = await createRre({
          data: passedData,
        }).unwrap();

        if (result.id) {
          reset();
          onClose();
          navigate(`/section-111/rres/${result.id}/overview`);
        }
      } catch (err) {
        console.error("Failed to create RRE:", err);
      }
    },
    [createRre, accessToken, reset, onClose, navigate],
  );

  const handleClose = useCallback(() => {
    reset();
    setClientSearchTerm(null);
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create RRE">
        <Controller
          control={control}
          name="client_id"
          rules={{ required: "Client is required" }}
          render={({ field: { name, onChange } }) => (
            <Select
              name={name}
              placeholder="Search..."
              label="Client"
              options={clientOptions}
              errors={errors}
              noOptionsMessage={({ inputValue }) =>
                !inputValue
                  ? "Begin typing to search for clients..."
                  : isClientsFetching
                    ? "Searching..."
                    : "No clients found."
              }
              onInputChange={(inputValue: string) =>
                debouncedSetClientSearchTerm(inputValue)
              }
              onChange={onChange}
            />
          )}
        />
        <Controller
          control={control}
          name="rre_id"
          rules={{ required: "CMS RRE ID is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Enter CMS RRE ID"
              label="CMS RRE ID"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="name"
          rules={{ required: "RRE Name is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Name"
              label="RRE Name"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="reporting_existed_prior_to_onboarding"
          render={({ field: { name, onChange } }) => (
            <Checkbox
              key={name}
              name={name}
              label="Has reporting history?"
              required={false}
              onChange={onChange}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateRREModal;
